import { DateTime } from 'luxon';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import getComparisonPreviousFiscalEntities from './getComparisonPreviousFiscalEntities';
import getComparisonLastYearFiscalEntities from './getComparisonLastYearFiscalEntitites';

/**
 * Takes an advanced date range input using fiscal ranges and returns the comparison period
 */
const getAdvancedFiscalComparison = (
  startDate: DateTime,
  endDate: DateTime | undefined,
  advancedRelativeDateRange: AdvancedRelativeDateRange,
  comparison: PersistedComparisonType,
  allFiscals?: FixedPeriods.AllFiscals,
): { startDate: DateTime | undefined; endDate: DateTime | undefined } => {
  if (!allFiscals) {
    const e = new Error();
    e.name = 'getAdvancedFiscalComparison: NoFiscalPeriodsFound';
    throw e;
  }

  const startInterval = advancedRelativeDateRange.from.interval;
  const endInterval = advancedRelativeDateRange.to.interval;
  let comparisonStartDate: DateTime | undefined;
  let comparisonEndDate: DateTime | undefined;

  // Get start date
  if (relativeDateRangeTypeCheckers.isFiscalInterval(startInterval)) {
    if (comparison.compareType === 'previous') {
      const intervalAmount =
        advancedRelativeDateRange.from.type === 'current'
          ? 1
          : advancedRelativeDateRange.from.n;
      comparisonStartDate = getComparisonPreviousFiscalEntities(
        startDate,
        intervalAmount,
        startInterval,
        false,
        allFiscals,
      ).startDate;
    } else if (comparison.compareType === 'lastYear') {
      comparisonStartDate = getComparisonLastYearFiscalEntities(
        startDate,
        endDate,
        startInterval,
        false,
        allFiscals,
      ).startDate;
    }
  }

  // Get end date
  if (relativeDateRangeTypeCheckers.isFiscalInterval(endInterval)) {
    if (comparison.compareType === 'previous') {
      const intervalAmount =
        advancedRelativeDateRange.to.type === 'current'
          ? 1
          : advancedRelativeDateRange.to.n;
      const comparison = getComparisonPreviousFiscalEntities(
        startDate,
        intervalAmount,
        endInterval,
        false,
        allFiscals,
      );

      if (!comparison.startDate || !comparison.endDate) {
        return { startDate: undefined, endDate: undefined };
      }

      if (advancedRelativeDateRange.config?.to === 'start') {
        comparisonEndDate = comparison.startDate;
      } else if (advancedRelativeDateRange.config?.to === 'end') {
        comparisonEndDate = comparison.endDate;
      } else {
        comparisonEndDate = comparison.startDate.minus({ days: 1 });
      }
    } else if (comparison.compareType === 'lastYear') {
      const comparison = getComparisonLastYearFiscalEntities(
        startDate,
        endDate,
        endInterval,
        false,
        allFiscals,
      );

      if (!comparison.startDate || !comparison.endDate) {
        return { startDate: undefined, endDate: undefined };
      }

      if (advancedRelativeDateRange.config?.to === 'start') {
        comparisonEndDate = comparison.startDate;
      } else if (advancedRelativeDateRange.config?.to === 'end') {
        comparisonEndDate = comparison.endDate;
      } else {
        comparisonEndDate = comparison.startDate?.minus({ days: 1 });
      }
    }
  } else if (comparisonStartDate) {
    // Get diff in weeks between start and end dates for current period
    const diffWeeks = (endDate || DateTime.now()).diff(
      startDate || DateTime.now(),
      'weeks',
    ).weeks;

    comparisonEndDate = comparisonStartDate.plus({ weeks: diffWeeks });
  }

  return { startDate: comparisonStartDate, endDate: comparisonEndDate };
};

export default getAdvancedFiscalComparison;
